import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BarChart, Bar, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { Badge } from '../ui/badge.tsx';
import { FiLink } from 'react-icons/fi';
import { supabase } from '../../supabase';
import { useAuth } from '../../AuthContext';
import { getMaxBacktestYears, getMaxStartDate, getTierDisplayName } from '../../utils/tiers';
import { createShareLink } from '../../lib/api/shareLinks';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4998';

const DEFAULT_ASSET_CLASS = 'stocks';
const ASSET_CLASS_OPTIONS = [
  { value: 'stocks', label: 'Stocks' },
  { value: 'etfs', label: 'ETFs' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'commodities', label: 'Commodities' }
];

const FOCUS_OPTIONS = [
  { value: 'annual', label: 'Annual Seasonality (Monthly Average)' },
  { value: 'weekday', label: 'Day of Week' }
];

const normalizeAssetClass = (value) => {
  const normalized = String(value || '').toLowerCase().trim();
  if (['stock', 'stocks', 'equity', 'equities'].includes(normalized)) return 'stocks';
  if (['etf', 'etfs'].includes(normalized)) return 'etfs';
  if (['crypto', 'cryptos', 'cryptoasset', 'cryptoassets'].includes(normalized)) return 'crypto';
  if (['commodity', 'commodities'].includes(normalized)) return 'commodities';
  return DEFAULT_ASSET_CLASS;
};

const buildDefaultDates = (billingTier) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = new Date(today);
  endDate.setDate(endDate.getDate() - 1);
  const startDate = getMaxStartDate(endDate, billingTier);
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    todayStr: today.toISOString().split('T')[0]
  };
};

const formatPercent = (value) => `${(value * 100).toFixed(2)}%`;

const SeasonalityPage = () => {
  const location = useLocation();
  const { billingTier } = useAuth() || {};
  const tier = billingTier || 'free';
  const tierLabel = getTierDisplayName(tier);
  const maxYears = getMaxBacktestYears(tier);

  const defaults = useMemo(() => buildDefaultDates(tier), [tier]);

  const [assetClass, setAssetClass] = useState(DEFAULT_ASSET_CLASS);
  const [symbols, setSymbols] = useState([]);
  const [filteredSymbols, setFilteredSymbols] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [focus, setFocus] = useState('annual');
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [todayStr, setTodayStr] = useState(defaults.todayStr);
  const [loadingSymbols, setLoadingSymbols] = useState(false);
  const [loadingSeasonality, setLoadingSeasonality] = useState(false);
  const [seasonality, setSeasonality] = useState(null);
  const [seasonalityResultId, setSeasonalityResultId] = useState(null);
  const [error, setError] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [shareFeedback, setShareFeedback] = useState(null);
  const [pendingSymbol, setPendingSymbol] = useState(null);

  const seasonalityResultParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('result_id') || params.get('seasonality_result_id');
  }, [location.search]);

  const maxStartDateStr = useMemo(() => {
    if (!endDate) return '';
    const parsed = new Date(endDate);
    if (Number.isNaN(parsed.getTime())) return '';
    return getMaxStartDate(parsed, tier).toISOString().split('T')[0];
  }, [endDate, tier]);

  useEffect(() => {
    const nextDefaults = buildDefaultDates(tier);
    setStartDate(nextDefaults.startDate);
    setEndDate(nextDefaults.endDate);
    setTodayStr(nextDefaults.todayStr);
  }, [tier]);

  useEffect(() => {
    if (!maxStartDateStr || !startDate) return;
    if (startDate < maxStartDateStr) {
      setStartDate(maxStartDateStr);
    }
  }, [maxStartDateStr, startDate]);

  useEffect(() => {
    const normalized = normalizeAssetClass(assetClass);
    const fetchSymbols = async () => {
      try {
        setLoadingSymbols(true);
        setError(null);
        const query = normalized ? `?asset_class=${encodeURIComponent(normalized)}` : '';
        const response = await fetch(`${API_BASE}/api/symbols${query}`);
        if (!response.ok) throw new Error('Failed to fetch symbols');
        const data = await response.json();
        const list = data.symbols || [];
        setSymbols(list);
        setFilteredSymbols(list);
        setSelectedSymbol('');
        setSearchTerm('');
      } catch (err) {
        setError(err.message || 'Failed to fetch symbols');
      } finally {
        setLoadingSymbols(false);
      }
    };
    fetchSymbols();
  }, [assetClass]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredSymbols(symbols);
      return;
    }
    const lower = searchTerm.toLowerCase();
    const filtered = symbols.filter((symbol) => (
      (symbol.ticker?.toLowerCase() ?? '').includes(lower) ||
      (symbol.company_name?.toLowerCase() ?? '').includes(lower) ||
      (symbol.name?.toLowerCase() ?? '').includes(lower) ||
      (symbol.sector?.toLowerCase() ?? '').includes(lower) ||
      (symbol.exchange?.toLowerCase() ?? '').includes(lower)
    ));
    setFilteredSymbols(filtered);
  }, [searchTerm, symbols]);

  useEffect(() => {
    if (!pendingSymbol || symbols.length === 0) return;
    const match = symbols.find((symbol) => String(symbol.ticker || '').toUpperCase() === pendingSymbol);
    if (match) {
      setSelectedSymbol(match.ticker);
    }
    setPendingSymbol(null);
  }, [pendingSymbol, symbols]);

  const focusLabel = useMemo(() => (
    FOCUS_OPTIONS.find((opt) => opt.value === focus)?.label || 'Seasonality'
  ), [focus]);

  const chartData = useMemo(() => seasonality?.points || [], [seasonality]);

  const applySeasonalityResult = useCallback((resultPayload, resultId) => {
    if (!resultPayload) return;
    const nextSymbol = (resultPayload.symbol || '').toUpperCase();
    if (nextSymbol) {
      setPendingSymbol(nextSymbol);
    }
    if (resultPayload.asset_class) {
      setAssetClass(normalizeAssetClass(resultPayload.asset_class));
    }
    if (resultPayload.focus) {
      setFocus(resultPayload.focus);
    }
    if (resultPayload.start_date) {
      setStartDate(resultPayload.start_date);
    }
    if (resultPayload.end_date) {
      setEndDate(resultPayload.end_date);
    }
    setSeasonality(resultPayload);
    setSeasonalityResultId(resultId || resultPayload.seasonality_result_id || resultPayload.id || null);
  }, []);

  useEffect(() => {
    if (!seasonalityResultParam) return;
    const fetchSeasonalityResult = async () => {
      try {
        setLoadingSeasonality(true);
        setError(null);
        setShareFeedback(null);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No active session');
        const response = await fetch(`${API_BASE}/api/seasonality-results/${seasonalityResultParam}`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || 'Failed to load seasonality result');
        }
        const record = data.seasonality_result || data.result || data;
        const payload = {
          ...(record.seasonality_results || {}),
          symbol: record.symbol || record.seasonality_results?.symbol,
          asset_class: record.asset_class || record.seasonality_results?.asset_class,
          focus: record.focus || record.seasonality_results?.focus,
          start_date: record.start_date || record.seasonality_results?.start_date,
          end_date: record.end_date || record.seasonality_results?.end_date,
          name: record.name || record.seasonality_results?.name
        };
        applySeasonalityResult(payload, record.id);
      } catch (err) {
        setError(err.message || 'Failed to load seasonality result');
      } finally {
        setLoadingSeasonality(false);
      }
    };
    fetchSeasonalityResult();
  }, [seasonalityResultParam, applySeasonalityResult]);

  const handleRunSeasonality = async () => {
    if (!selectedSymbol) {
      setError('Please select an asset.');
      return;
    }
    if (!startDate || !endDate) {
      setError('Please select a start and end date.');
      return;
    }
    try {
      setLoadingSeasonality(true);
      setError(null);
      setShareFeedback(null);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const payload = {
        symbol: selectedSymbol,
        asset_class: normalizeAssetClass(assetClass),
        start_date: startDate,
        end_date: endDate,
        focus
      };

      const response = await fetch(`${API_BASE}/api/seasonality`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to run seasonality');
      }
      const resultPayload = data.result || data;
      setSeasonality(resultPayload);
      setSeasonalityResultId(resultPayload?.seasonality_result_id || null);
    } catch (err) {
      setError(err.message || 'Failed to run seasonality');
    } finally {
      setLoadingSeasonality(false);
    }
  };

  const handleShare = async () => {
    if (!seasonalityResultId) return;
    setShareFeedback(null);
    try {
      setSharing(true);
      const shareId = await createShareLink('seasonality_result', seasonalityResultId);
      const shareUrl = `${window.location.origin}/share/${shareId}`;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        window.prompt('Copy share link:', shareUrl);
      }
      setShareFeedback({ message: 'Share link copied to clipboard.' });
    } catch (err) {
      setShareFeedback({
        message: err.message || 'Failed to create share link',
        isError: true
      });
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="seasonality-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Seasonality</h1>
          <p className="text-gray-600">
            Compare average returns across calendar windows for a single asset.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {seasonalityResultId && (
            <Button
              variant="outline"
              onClick={handleShare}
              disabled={sharing}
              className="flex items-center gap-2 px-4 py-2"
            >
              <FiLink />
              {sharing ? 'Sharing...' : 'Share'}
            </Button>
          )}
          <Button
            onClick={handleRunSeasonality}
            disabled={loadingSeasonality || !selectedSymbol}
            className="px-5 py-2"
          >
            {loadingSeasonality ? 'Running...' : 'Run Seasonality'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {shareFeedback && (
        <div className={`mb-6 rounded-lg border p-3 text-sm ${shareFeedback.isError ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
          {shareFeedback.message}
        </div>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Seasonality Inputs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="seasonality-asset-class">Asset Class</Label>
              <select
                id="seasonality-asset-class"
                value={assetClass}
                onChange={(e) => setAssetClass(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              >
                {ASSET_CLASS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="seasonality-focus">Seasonality Focus</Label>
              <select
                id="seasonality-focus"
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              >
                {FOCUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="seasonality-search">Find Asset</Label>
              <Input
                id="seasonality-search"
                placeholder="Search ticker, name, or sector"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loadingSymbols}
              />
              <select
                value={selectedSymbol}
                onChange={(e) => setSelectedSymbol(e.target.value)}
                disabled={loadingSymbols || filteredSymbols.length === 0}
                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              >
                <option value="">{loadingSymbols ? 'Loading assets...' : 'Select an asset'}</option>
                {filteredSymbols.slice(0, 500).map((symbol) => {
                  const name = symbol.company_name || symbol.name || symbol.ticker;
                  return (
                    <option key={symbol.ticker} value={symbol.ticker}>
                      {symbol.ticker}{name ? ` - ${name}` : ''}
                    </option>
                  );
                })}
              </select>
              <div className="text-xs text-gray-500">
                {filteredSymbols.length} matching assets
              </div>
            </div>
            <div className="space-y-2">
              <Label>Timeframe</Label>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="seasonality-start" className="text-xs text-gray-600">Start</Label>
                  <Input
                    id="seasonality-start"
                    type="date"
                    value={startDate}
                    min={maxStartDateStr}
                    max={endDate || todayStr}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="seasonality-end" className="text-xs text-gray-600">End</Label>
                  <Input
                    id="seasonality-end"
                    type="date"
                    value={endDate}
                    min={startDate || maxStartDateStr}
                    max={todayStr}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Your {tierLabel} plan allows up to {maxYears} years. Earliest allowed start date: {maxStartDateStr || 'N/A'}.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {seasonality && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle>{seasonality.symbol} Seasonality</CardTitle>
                <div className="mt-1 text-sm text-gray-600">
                  {seasonality.start_date} to {seasonality.end_date} | {focusLabel} | {seasonality.sample_count} samples
                </div>
              </div>
              {seasonality.dividends_included && (
                <Badge className="bg-green-100 text-green-700">Dividends Included</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis tickFormatter={formatPercent} />
                  <Tooltip
                    formatter={(value) => formatPercent(value)}
                  />
                  <ReferenceLine y={0} stroke="#94a3b8" />
                  <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SeasonalityPage;
